import React from 'react'
import { FileText, Download, Eye, Calendar, User, X } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../ui'

export interface VehicleDocument {
 id: string
 type: string
 name: string
 url: string
 expiryDate?: string
 uploadedBy: {
 id: string
 name: string
 email: string
 }
 createdAt: string
}

interface VehicleExistingDocumentsProps {
 documents: VehicleDocument[]
 onRemoveDocument?: (docId: string) => void
 readOnly?: boolean
}

const documentTypeLabels: Record<string, string> = {
 crlv: 'CRLV (Certificado de Registro)',
 invoice: 'Nota Fiscal',
 inspection: 'Laudo de Vistoria',
 transfer: 'Documento de Transferência',
 ipva: 'Comprovante IPVA',
 licensing: 'Licenciamento',
 other: 'Outros'
}

const documentTypeColors: Record<string, string> = {
 crlv: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
 invoice: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
 inspection: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
 transfer: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
 ipva: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
 licensing: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
 other: 'bg-surface text-foreground'
}

export const VehicleExistingDocuments: React.FC<VehicleExistingDocumentsProps> = ({
 documents,
 onRemoveDocument,
 readOnly = false
}) => {
 const handleViewDocument = (url: string) => {
 window.open(url, '_blank')
 }

 const handleDownloadDocument = (url: string, name: string) => {
 const a = document.createElement('a')
 a.href = url
 a.download = name
 a.target = '_blank'
 a.click()
 }

 if (documents.length === 0) {
 return (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center">
 <FileText className="h-5 w-5 mr-2" />
 Documentos do Veículo
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="text-center py-8">
 <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
 <p className="text-muted-foreground">
 Nenhum documento cadastrado
 </p>
 </div>
 </CardContent>
 </Card>
 )
 }

 return (
 <Card>
 <CardHeader>
 <CardTitle className="flex items-center justify-between">
 <div className="flex items-center">
 <FileText className="h-5 w-5 mr-2" />
 Documentos do Veículo
 </div>
 <span className="text-sm font-normal text-muted-foreground">
 {documents.length} {documents.length === 1 ? 'documento' : 'documentos'}
 </span>
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-3">
 {documents.map((doc) => (
 <div
 key={doc.id}
 className="p-4 bg-card border border-border rounded-lg hover:border-accent dark:hover:border-primary-400 transition-colors"
 >
 <div className="flex items-start justify-between gap-4">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-2">
 <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
 <h4 className="text-sm font-semibold text-foreground truncate">
 {doc.name}
 </h4>
 </div>
 
 {/* Badge de categoria */}
 <div className="mb-2">
 <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${documentTypeColors[doc.type] || documentTypeColors.other}`}>
 {documentTypeLabels[doc.type] || doc.type}
 </span>
 </div>

 {/* Informações adicionais */}
 <div className="space-y-1 text-xs text-muted-foreground">
 <div className="flex items-center gap-1">
 <User className="h-3 w-3" />
 <span>Enviado por: {doc.uploadedBy.name}</span>
 </div>
 <div className="flex items-center gap-1">
 <Calendar className="h-3 w-3" />
 <span>
 {new Date(doc.createdAt).toLocaleDateString('pt-BR', {
 day: '2-digit',
 month: 'short',
 year: 'numeric'
 })}
 </span>
 </div>
 {doc.expiryDate && (
 <div className="flex items-center gap-1">
 <Calendar className="h-3 w-3" />
 <span>
 Validade: {new Date(doc.expiryDate).toLocaleDateString('pt-BR', {
 day: '2-digit',
 month: 'short',
 year: 'numeric'
 })}
 </span>
 </div>
 )}
 </div>
 </div>

 {/* Botões de ação */}
 <div className="flex flex-col gap-2 flex-shrink-0">
 <button
 onClick={() => handleViewDocument(doc.url)}
 className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
 title="Visualizar documento"
 >
 <Eye className="h-4 w-4" />
 </button>
 <button
 onClick={() => handleDownloadDocument(doc.url, doc.name)}
 className="p-2 text-muted-foreground hover:bg-surface rounded-lg transition-colors"
 title="Baixar documento"
 >
 <Download className="h-4 w-4" />
 </button>
 {!readOnly && onRemoveDocument && (
 <button
 onClick={() => onRemoveDocument(doc.id)}
 className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors"
 title="Remover documento"
 >
 <X className="h-4 w-4" />
 </button>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 )
}

